import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft, Loader2, Eye, EyeOff,
  CheckCircle2, CreditCard, FileText, ShieldCheck,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import {
  trackProfessionalSignupStarted,
  trackProfessionalDocumentsCompleted,
  trackCompleteRegistration,
} from '@/utils/analytics';
import { getUtmParams } from '@/utils/utm';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// ---- Fila compacta para cada documento ----
function DocRow({ label, Icon, accept, file, onFileSelect, testId, hasError, errorMsg }) {
  const done = Boolean(file);
  const error = hasError && !done;

  return (
    <div
      className={`flex items-center gap-3 rounded-xl p-3.5 border transition-all duration-200 ${
        done  ? 'border-green-400 bg-green-50'
        : error ? 'border-red-300 bg-red-50'
        : 'border-gray-200 bg-white'
      }`}
    >
      {/* Ícono */}
      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
        done ? 'bg-green-100' : error ? 'bg-red-100' : 'bg-gray-100'
      }`}>
        {done
          ? <CheckCircle2 className="w-5 h-5 text-green-600" />
          : <Icon className={`w-5 h-5 ${error ? 'text-red-400' : 'text-gray-400'}`} />}
      </div>

      {/* Contenido */}
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`text-sm font-semibold leading-tight ${error ? 'text-red-700' : 'text-gray-900'}`}>
            {label}
          </span>
          <span className="text-red-400 text-xs leading-none">*</span>
          {done ? (
            <span className="px-1.5 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
              Completado
            </span>
          ) : (
            <span className={`px-1.5 py-0.5 rounded-full text-xs font-semibold ${
              error ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'
            }`}>
              Pendiente
            </span>
          )}
        </div>
        {done ? (
          <p className="text-xs text-green-600 mt-0.5 truncate">{file.name}</p>
        ) : error ? (
          <p className="text-xs text-red-500 mt-0.5">{errorMsg}</p>
        ) : null}
      </div>

      {/* Botón de carga */}
      <label className={`flex-shrink-0 cursor-pointer px-3 py-1.5 rounded-lg text-xs font-semibold border transition-all ${
        done
          ? 'border-green-400 text-green-700 hover:bg-green-100'
          : 'border-orange-400 text-orange-600 hover:bg-orange-50'
      }`}>
        {done ? 'Cambiar' : 'Seleccionar'}
        <input
          type="file"
          accept={accept}
          onChange={onFileSelect}
          className="hidden"
          data-testid={testId}
        />
      </label>
    </div>
  );
}

// ---- Configuración de documentos ----
const DOC_CONFIG = [
  {
    key: 'dni_front',
    label: 'DNI Frente',
    Icon: CreditCard,
    accept: 'image/*',
    type: 'image',
    testId: 'doc-dni-front',
    errorMsg: 'Necesitamos una foto del frente del DNI',
  },
  {
    key: 'dni_back',
    label: 'DNI Dorso',
    Icon: CreditCard,
    accept: 'image/*',
    type: 'image',
    testId: 'doc-dni-back',
    errorMsg: 'Necesitamos una foto del dorso del DNI',
  },
  {
    key: 'criminal_record',
    label: 'Antecedentes Penales',
    Icon: FileText,
    accept: 'application/pdf',
    type: 'pdf',
    testId: 'doc-criminal-record',
    errorMsg: 'Necesitamos el certificado de antecedentes penales',
  },
];

export default function ProviderAuthPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const ticketId = searchParams.get('ticketId');

  const [mode, setMode] = useState('login');
  const [submitting, setSubmitting] = useState(false);
  const [submitStep, setSubmitStep] = useState('');
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const [form, setForm] = useState({ email: '', username: '', phone: '', password: '' });
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [docs, setDocs] = useState({ dni_front: null, dni_back: null, criminal_record: null });

  const docsCount = DOC_CONFIG.filter(({ key }) => docs[key]).length;

  // ---- Guards de eventos de analytics (un solo disparo por intención) ----
  const signupStartedFired = useRef(false);
  const docsCompletedFired = useRef(false);

  // ProfessionalSignupStarted: solo cuando el usuario entra en modo Registro
  useEffect(() => {
    if (mode === 'register' && !signupStartedFired.current) {
      signupStartedFired.current = true;
      trackProfessionalSignupStarted();
    }
  }, [mode]);

  // ProfessionalDocumentsCompleted: solo la primera vez que se completan los 3 docs
  useEffect(() => {
    if (docsCount === 3 && !docsCompletedFired.current) {
      docsCompletedFired.current = true;
      trackProfessionalDocumentsCompleted();
    }
  }, [docsCount]);

  // ---- Validación de archivos (sin cambios) ----
  const handleFileChange = (e, docKey, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (type === 'image') {
      if (!file.type.startsWith('image/')) { toast.error('Debe ser una imagen (JPG, PNG, WEBP)'); e.target.value = ''; return; }
      if (file.size > 10 * 1024 * 1024) { toast.error('La imagen no puede superar los 10MB'); e.target.value = ''; return; }
    }
    if (type === 'pdf') {
      if (file.type !== 'application/pdf') { toast.error('Debe ser un archivo PDF'); e.target.value = ''; return; }
      if (file.size > 15 * 1024 * 1024) { toast.error('El PDF no puede superar los 15MB'); e.target.value = ''; return; }
    }
    setDocs((prev) => ({ ...prev, [docKey]: file }));
  };

  // ---- Submit unificado (sin cambios) ----
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (mode === 'register') {
      setSubmitAttempted(true);
      if (!docs.dni_front || !docs.dni_back || !docs.criminal_record) return;
    }
    setSubmitting(true);
    try {
      if (mode === 'login') {
        const { data } = await axios.post(`${API}/providers/login`, { email: form.email, password: form.password });
        localStorage.setItem('provider_token', data.token);
        localStorage.setItem('provider_data', JSON.stringify(data.provider));
        toast.success('¡Bienvenido!');
        navigate(ticketId ? `/profesionales?autoReveal=${ticketId}` : '/profesionales', { replace: true });
      } else {
        setSubmitStep('creating');
        const { data } = await axios.post(`${API}/providers/register`, {
          email: form.email, username: form.username, phone: form.phone,
          password: form.password, accepted_terms: acceptedTerms,
          ...getUtmParams(),
        });
        const token = data.token;
        localStorage.setItem('provider_token', token);
        localStorage.setItem('provider_data', JSON.stringify(data.provider));

        setSubmitStep('uploading');
        const formData = new FormData();
        formData.append('dni_front', docs.dni_front);
        formData.append('dni_back', docs.dni_back);
        formData.append('criminal_record', docs.criminal_record);
        const docsResponse = await axios.post(`${API}/providers/documents`, formData, { headers: { Authorization: `Bearer ${token}` } });

        // CompleteRegistration: solo cuando register + documents + aprobación automática son exitosos
        if (docsResponse.data?.status === 'success') {
          trackCompleteRegistration();
        }

        toast.success('¡Cuenta verificada! Completá tu perfil profesional.');
        navigate(ticketId ? `/completar-perfil?ticketId=${ticketId}` : '/completar-perfil', { replace: true });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al procesar la solicitud');
    } finally {
      setSubmitting(false);
      setSubmitStep('');
    }
  };

  const switchMode = () => {
    setMode((m) => (m === 'login' ? 'register' : 'login'));
    setForm({ email: '', username: '', phone: '', password: '' });
    setAcceptedTerms(false);
    setDocs({ dni_front: null, dni_back: null, criminal_record: null });
    setSubmitAttempted(false);
    // Resetear guards de docs para nueva sesión de registro
    docsCompletedFired.current = false;
  };

  const canSubmit =
    mode === 'login'
      ? Boolean(form.email && form.password)
      : Boolean(form.email && form.username && form.phone && form.password.length >= 8 &&
          docs.dni_front && docs.dni_back && docs.criminal_record && acceptedTerms);

  const submitLabel = submitting
    ? submitStep === 'creating' ? 'Creando cuenta...'
    : submitStep === 'uploading' ? 'Verificando documentos...'
    : 'Procesando...'
    : mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta';

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col" data-testid="provider-auth-page">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-4 sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center gap-3">
          <button data-testid="back-btn" onClick={() => navigate('/profesionales')}
            className="p-2 rounded-lg hover:bg-gray-100 transition-colors -ml-2" aria-label="Volver">
            <ArrowLeft className="w-5 h-5 text-gray-700" />
          </button>
          <span className="text-base font-semibold text-gray-900">
            {mode === 'login' ? 'Iniciar sesión' : 'Crear cuenta'}
          </span>
        </div>
      </header>

      <main className="flex-1 px-4 py-8 pb-16">
        <div className="max-w-md mx-auto">
          {/* Título */}
          <div className="mb-8">
            <h1 className="text-2xl font-bold text-gray-900 mb-2">
              {mode === 'login' ? 'Bienvenido de vuelta' : 'Creá tu cuenta'}
            </h1>
            <p className="text-gray-500 text-sm leading-relaxed">
              {ticketId
                ? mode === 'login' ? 'Iniciá sesión para ver los datos de contacto del cliente'
                : 'Registrate para acceder a los datos de contacto del cliente'
                : mode === 'login' ? 'Ingresá para ver los trabajos disponibles'
                : 'Registrate como proveedor y verificá tu identidad para activar tu cuenta'}
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Email */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="auth-email">Email</label>
              <input id="auth-email" data-testid="auth-email" type="email" inputMode="email"
                autoComplete="email" placeholder="tu@email.com" value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })} required
                className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
            </div>

            {/* Usuario — solo registro */}
            {mode === 'register' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="auth-username">Nombre de usuario</label>
                <input id="auth-username" data-testid="auth-username" type="text" autoComplete="username"
                  placeholder="tu_nombre" value={form.username}
                  onChange={(e) => setForm({ ...form, username: e.target.value })}
                  required minLength={3} maxLength={30}
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
              </div>
            )}

            {/* Teléfono — solo registro */}
            {mode === 'register' && (
              <div className="space-y-1.5">
                <label className="text-sm font-medium text-gray-700" htmlFor="auth-phone">Número de teléfono</label>
                <input id="auth-phone" data-testid="auth-phone" type="tel" inputMode="tel"
                  autoComplete="tel" placeholder="Ej: 11 2345-6789" value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  required minLength={8} maxLength={20} pattern="[\d\s\-\+\(\)]{8,20}"
                  title="Ingresá un número de teléfono válido"
                  className="w-full px-4 py-3 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
              </div>
            )}

            {/* Contraseña */}
            <div className="space-y-1.5">
              <label className="text-sm font-medium text-gray-700" htmlFor="auth-password">Contraseña</label>
              <div className="relative">
                <input id="auth-password" data-testid="auth-password"
                  type={showPassword ? 'text' : 'password'}
                  autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  placeholder={mode === 'register' ? 'mínimo de 8 caracteres' : 'tu contraseña'}
                  value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                  required minLength={8}
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl text-base focus:outline-none focus:ring-2 focus:ring-orange-400 bg-white" />
                <button type="button" data-testid="toggle-password-btn"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                  tabIndex={-1} aria-label={showPassword ? 'Ocultar contraseña' : 'Mostrar contraseña'}>
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
              {mode === 'register' && (
                <p className="text-xs text-gray-400">Mínimo de 8 caracteres.</p>
              )}
            </div>

            {/* ===== SECCIÓN DE DOCUMENTACIÓN — solo registro ===== */}
            {mode === 'register' && (
              <>
                {/* Encabezado de sección */}
                <div className="pt-1">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="flex-1 h-px bg-gray-200" />
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      Documentación Requerida
                    </span>
                    <div className="flex-1 h-px bg-gray-200" />
                  </div>
                  <p className="text-xs text-gray-500 text-center leading-relaxed">
                    Para proteger a los clientes y profesionales, necesitamos verificar tu identidad.
                  </p>
                </div>

                {/* Filas de documentos */}
                <div className="space-y-2.5" data-testid="docs-section">
                  {DOC_CONFIG.map(({ key, label, Icon, accept, type, testId, errorMsg }) => (
                    <DocRow
                      key={key}
                      label={label}
                      Icon={Icon}
                      accept={accept}
                      file={docs[key]}
                      onFileSelect={(e) => handleFileChange(e, key, type)}
                      testId={testId}
                      hasError={submitAttempted && !docs[key]}
                      errorMsg={errorMsg}
                    />
                  ))}
                </div>

                {/* Indicador global de progreso */}
                <div
                  data-testid="docs-progress"
                  className={`flex items-center justify-between px-4 py-3 rounded-xl border transition-all duration-300 ${
                    docsCount === 3 ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
                  }`}
                >
                  {docsCount === 3 ? (
                    <div className="flex items-center gap-2">
                      <ShieldCheck className="w-4 h-4 text-green-600" />
                      <span className="text-sm font-semibold text-green-700">Documentación completa</span>
                    </div>
                  ) : (
                    <p className="text-sm font-medium text-gray-600">Documentación completada</p>
                  )}
                  <span className={`text-xl font-bold tabular-nums transition-colors ${
                    docsCount === 3 ? 'text-green-600'
                    : docsCount > 0 ? 'text-orange-500'
                    : 'text-gray-300'
                  }`}>
                    {docsCount}/3
                  </span>
                </div>

                {/* Términos */}
                <div className="flex items-start space-x-2 pt-1">
                  <Checkbox id="provider-terms" data-testid="provider-terms-checkbox"
                    checked={acceptedTerms} onCheckedChange={(c) => setAcceptedTerms(!!c)} />
                  <label htmlFor="provider-terms" className="text-sm leading-relaxed cursor-pointer text-gray-600">
                    Acepto los términos y condiciones. Mi información será compartida con clientes que publiquen trabajos.
                  </label>
                </div>
              </>
            )}

            {/* Botón principal — el wrapper detecta click en estado deshabilitado */}
            <div onClick={() => mode === 'register' && setSubmitAttempted(true)}>
              <button
                data-testid="auth-submit-btn"
                type="submit"
                disabled={!canSubmit || submitting}
                className="w-full py-3.5 rounded-xl font-semibold text-white text-base transition-opacity disabled:opacity-50 flex items-center justify-center gap-2 mt-2"
                style={{ backgroundColor: 'var(--primary-strong)' }}
              >
                {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                {submitLabel}
              </button>
            </div>
          </form>

          {/* Cambiar modo */}
          <div className="mt-6 text-center border-t pt-6">
            <button type="button" data-testid="auth-switch-btn" onClick={switchMode}
              className="text-sm text-gray-500 hover:text-gray-800 hover:underline transition-colors">
              {mode === 'login' ? '¿No tenés cuenta? Registrate' : '¿Ya tenés cuenta? Iniciá sesión'}
            </button>
          </div>
        </div>
      </main>
    </div>
  );
}
